import {SubmissionKind} from "../../../../api/Submissions";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useProject, useProjectId} from "../ProjectName";
import {distinct, groupByArr, sum} from "nate-react-api-helpers";
import {AliasNeeded, AliasUpdate} from "../../quote/pricing/AliasNeeded";
import {ProjectContext} from "../../ProjectProvider";
import {PDFPrintInfo, usePrintParallel} from "../changeset/PrintParallel";
import {api} from "../../../../api/API";
import {PriceLine2} from "../../../../api/Pricing";
import {MakePriceTable, PriceKind, PriceRequestPDF} from "../../quote/pricing/PriceRequestPDF";
import {SmallGreyButton} from "../../../../misc/SmallGreyButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
    Button,
    Checkbox, CircularProgress,
    DialogActions,
    DialogTitle,
    Divider,
    List,
    ListItemButton,
    ListItemIcon, ListItemText,
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {downloadCSV} from "../../../logistics/purchasing/csvutil";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {useGetAll} from "../../../logistics/routes/useGetAll";
import {Project} from "../../../../api/Projects";
import {sanitizeFileName} from "../../../logistics/routes/RouteItems";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {MenuButton} from "../../../logistics/purchasing/MenuButton";

export function PriceRequestSubmittal(props: {
    tab: string;
    submissionTypeOverride?: SubmissionKind
}) {
    const [show, setShow] = useState(false);
    const parallelPrint = usePrintParallel();

    if(props.tab === "summary") return null;

    return (
        <>
            <SmallGreyButton onClick={() => setShow(true)} startIcon={<AddCircleIcon />}>
                Price Request
            </SmallGreyButton>
            {show && <MakeSubmittal loading={parallelPrint.loading} printer={parallelPrint.printer} tab={props.tab} onClose={() => setShow(false)} />}
        </>
    )
}

function MakeSubmittal(props: {
    tab: string;
    loading: boolean;
    printer(input: PDFPrintInfo): void;
    onClose(): void;
}) {
    const [alias, setAlias] = useState<AliasUpdate>(null);
    const project = useProject();
    const projectId = useProjectId();

    const [checked, setChecked] = useState<string[]>([])
    const projectCtx = useContext(ProjectContext);

    const req = useGetAll(input => api.pricing.list(input), {
        project: projectId,
        category: props.tab as any,
    }, [projectId, props.tab]);
    const data = req.asList;

    const groups = useMemo(() => {
        type Group = {
            key: string;
            name: string;
            kind: PriceKind;
            list: PriceLine2[];
        }

        let list: Group[] = [];

        if(props.tab === "hardware") {
            groupByArr(data, v => v.manufacturer).forEach(group => {
                list.push({
                    key: sanitizeFileName(group.values[0].manufacturerName.toLowerCase()),
                    name: group.values[0].manufacturerName,
                    list: group.values,
                    kind: "hardware",
                })
            })
        } else if(props.tab === "div10") {
            groupByArr(data, v => v.category || "").forEach(group => {
                list.push({
                    key: "div10/" + group.key,
                    name: group.key,
                    list: group.values,
                    kind: "div10/" + group.key as any
                });
            })
        } else {
            let key = "unknown";
            let name = "unknown";
            let kind: PriceKind = "door";

            switch (props.tab) {
            case "door-hm":
                name = "Hollow Metal Door"
                key = props.tab;
                break;
            case "door-wd":
                name = "Wood Door"
                key = props.tab;
                break;
            case "frame-hm":
                name = "Hollow Metal Frame"
                key = props.tab;
                kind = "frame";
                break;
            case "frame-anchor":
                name = "Frame Anchors"
                key = props.tab;
                kind = "hardware";
                break;
            }

            list.push({
                key: key,
                kind: kind,
                name: name,
                list: data,
            })
        }

        if(list.length === 1) {
            setChecked([list[0].key]);
        }

        return list;
    }, [data, props.tab]);

    const onSubmit = async () => {
        let projectObj: Project = project as any;
        if (alias && alias.updateNeeded) {
            let project = await api.projects.get(projectId);
            await api.projects.upsert(Object.assign({}, project, {
                nameAlias: alias.value,
            }));
            projectCtx.reload();
            projectObj = await api.projects.get(projectId);
        }


        let listsByKey: {[key: string]: {list: PriceLine2[], key: string, kind: PriceKind}} = {};
        groups.forEach(g => {
            if(checked.indexOf(g.key) !== -1) {
                listsByKey[g.key] = {
                    list: g.list,
                    key: g.key,
                    kind: g.kind,
                }
            }
        })

        return {
            listsByKey,
            project: projectObj,
        }
    }

    const onCloseRef = useSyncedRef(props.onClose)
    const loadingRef = useRef(props.loading);
    useEffect(() => {
        if(loadingRef.current && !props.loading) {
            onCloseRef.current();
        }

        loadingRef.current = props.loading;
    }, [props.loading, onCloseRef]);

    if(!project) return null;

    const submit = async () => {
        const print = (key: string, kind: PriceKind, list: PriceLine2[]) => {
            const appendFiles = distinct(list
                .map(l => l.frame?.screenElevFile || 0)
                .filter(v => !!v) as number[])

            props.printer({
                name: "price-request.pdf",
                render: async onReady => <PriceRequestPDF
                    keyName={key}
                    kind={kind}
                    project={project}
                    onReady={onReady}
                    items={list}
                />,
                serverInfo: {
                    submissionForProject: project.id,
                    submissionType: "price-request",
                    ignoreSubmissionDate: true,
                    appendFiles: appendFiles,
                },
                download: true,
            })
        }

        const {listsByKey} = await onSubmit();

        for(let i in listsByKey) {
            const obj = listsByKey[i];
            print(obj.key, obj.kind, combineIdentical(obj.list));
        }
    };

    return (
        <Dialog2 open onClose={() => props.onClose()} onEnterPress={submit}>
            <DialogTitle>Price Request Submittal</DialogTitle>
            <AliasNeeded project={project} onChange={a => setAlias(a)} />
            <Divider />

            <List dense disablePadding>
                {groups.map(c => {
                    return (<>
                        <ListItemButton
                            key={c.key}
                            onClick={e => {
                                let updated = checked.slice(0);

                                if(checked.indexOf(c.key) !== -1) {
                                    updated = updated.filter(val => val !== c.key);
                                } else {
                                    updated.push(c.key);
                                }

                                setChecked(updated);
                            }}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    size="small"
                                    tabIndex={-1}
                                    disableRipple
                                    checked={checked.indexOf(c.key) !== -1}
                                />
                            </ListItemIcon>
                            <ListItemText style={{margin: 0, paddingRight: 16}}
                                          primary={c.name}
                                          secondary={sum(c.list.map(v => v.qty)) + " items"}
                            />
                        </ListItemButton>
                    </>)})}
            </List>
            <DialogActions>
                <MenuButton size="small" variant="outlined"
                            options={[{
                                    name: "Combined File",
                                    onClick: async () => {
                                        const {listsByKey} = await onSubmit();
                                        const byKind: {
                                            [kind: string]: {
                                                list: PriceLine2[],
                                            }
                                        } = {};

                                        for(let i in listsByKey) {
                                            const obj = listsByKey[i];

                                            let v = byKind[obj.kind];
                                            if(!v) {
                                                v = {
                                                    list: [],
                                                }

                                                byKind[obj.kind] = v;
                                            }

                                            v.list.push(...obj.list);
                                        }


                                        for(let i in byKind) {
                                            const tbl = MakePriceTable({
                                                kind: i as any,
                                                list: combineIdentical(byKind[i].list),
                                                keyName: i,
                                                projectSys: project?.unitSystem || "imperial",
                                            });

                                            downloadCSV(`price-request-${i}.csv`, tbl.toStringArray());
                                        }


                                        props.onClose();
                                    },
                                }, {
                                    name: "Separate Files (per manufacturer)",
                                    onClick: async () => {
                                        const {listsByKey} = await onSubmit();

                                        for(let i in listsByKey) {
                                            const obj = listsByKey[i];

                                            console.log(obj);

                                            const tbl = MakePriceTable({
                                                kind: obj.kind,
                                                list: combineIdentical(obj.list),
                                                keyName: i,
                                                projectSys: project?.unitSystem || "imperial",
                                            });

                                            downloadCSV(`price-request-${obj.key}.csv`, tbl.toStringArray());
                                        }

                                        props.onClose();
                                    },
                                }]}
                        >Download Excel</MenuButton>
                <div style={{flex: 1}} />
                {props.loading && <CircularProgress />}
                <Button onClick={() => props.onClose()}>Cancel</Button>
                <Button size="small" variant="contained"
                        disabled={props.loading}
                        color="primary" startIcon={<CheckCircleIcon />}
                        onClick={submit}
                >Create and Download</Button>
            </DialogActions>
        </Dialog2>
    )
}

// capitalizes the first letter of each word
export function ucWords(input: string) {
    return input.split(" ").map(v => {
        if(v.length > 1) return v[0].toUpperCase() + v.slice(1)
        return v;
    }).join(" ");
}

function combineIdentical(list: PriceLine2[]) {
    const out: PriceLine2[] = [];
    list.forEach(item => {
        const match = out.find(v => v.product === item.product)
        if(!match) {
            out.push(Object.assign({}, item));
            return;
        }

        match.qty += item.qty;
    })

    return out;
}